import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { MdCloudUpload, MdDelete, MdEdit, MdCreateNewFolder, MdDriveFileMove, MdClose } from 'react-icons/md';

const apiBase = process.env.REACT_APP_PAGES_API || '/api';

export default function AdminUpload() {
  const { role, user } = useAuth ? useAuth() : { role: null, user: null };
  // basic guard: if not admin/owner, show message
  if (!(role === 'Majitel' || role === 'Admin')) {
    return <CenteredMessage>Přístup pouze pro administrátory.</CenteredMessage>;
  }

  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [files, setFiles] = useState([]);

  const [newFolderName, setNewFolderName] = useState('');
  const [showConfirm, setShowConfirm] = useState(null); // { kind: 'folder'|'file', id, title, action }
  const [showRename, setShowRename] = useState(null); // { kind: 'folder'|'file', id, currentName }

  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [processing, setProcessing] = useState(false); // server-side forward in progress
  const [error, setError] = useState('');

  const folderNameRef = useRef(null);
  const fileInputRef = useRef(null);
  const moveRefs = useRef({});
  const esRef = useRef(null);

  useEffect(() => { loadFolders(); }, []);
  useEffect(() => { if (selectedFolder) loadFiles(selectedFolder.id); else setFiles([]); }, [selectedFolder]);
  // focus the rename input when rename modal opens
  useEffect(() => {
    if (showRename) {
      setTimeout(() => {
        try { const el = document.getElementById('rename-input'); if (el) { el.focus(); el.select && el.select(); } } catch(_){}
      }, 50);
    }
  }, [showRename]);

  async function loadFolders(){
    try{
      const r = await fetch(`${apiBase}/filemoon/folders`);
      if(!r.ok) throw new Error('failed');
      const j = await r.json();
      setFolders(Array.isArray(j)?j:[]);
      if(!selectedFolder && Array.isArray(j) && j.length) setSelectedFolder(j[0]);
    }catch(e){ console.warn('folders load err', e); setFolders([]); }
  }

  async function loadFiles(folderId){
    try{
      const r = await fetch(`${apiBase}/filemoon/files?folderId=${encodeURIComponent(folderId)}`);
      if(!r.ok) throw new Error('failed');
      const j = await r.json();
      setFiles(Array.isArray(j)?j:[]);
    }catch(e){ console.warn('files load err', e); setFiles([]); }
  }

  async function createFolder(){
    if(!newFolderName) return;
    try{
      const r = await fetch(`${apiBase}/filemoon/folders`, { method:'POST', headers:{'content-type':'application/json'}, body: JSON.stringify({ name: newFolderName }) });
      const txt = await r.text();
      if(!r.ok){
        let msg = txt;
        try{ const j = JSON.parse(txt); msg = j && (j.error || j.message) ? (j.error || j.message) : JSON.stringify(j); }catch(_){}
        setError(`Nelze vytvořit složku: ${r.status} ${msg}`);
        return;
      }
      setNewFolderName('');
      await loadFolders();
    }catch(e){ console.error('createFolder error', e); setError('Nelze vytvořit složku: ' + (e && e.message ? e.message : 'chyba')); }
  }

  async function renameFolder(id){
    // open rename modal for folder
    const current = folders.find(f=>f.id===id);
    setShowRename({ kind: 'folder', id, currentName: current ? current.name : '' });
  }

  async function deleteFolder(id){
    // open confirm modal
    const current = folders.find(f=>f.id===id);
    setShowConfirm({ kind: 'folder', id, title: current ? current.name : '' });
  }

  function handleFileChange(e){ const f = e.target.files && e.target.files[0]; setFile(f||null); if(f && !title) setTitle(f.name||''); }

  function upload(){
    if(!file){ setError('Vyber soubor.'); return; }
    setError(''); setUploading(true); setProgress(0);
    setProcessing(false);
    const url = `${apiBase}/filemoon/upload`;
    const fd = new FormData(); fd.append('file', file); fd.append('title', title || file.name || '');
    if(user && user.uid) fd.append('userId', user.uid);
    if(selectedFolder && selectedFolder.id) fd.append('folderId', selectedFolder.id);

  // create an uploadId and open SSE to receive server-side forwarding progress
  const uploadId = `u_${Date.now()}_${Math.floor(Math.random()*100000)}`;
  try{ if(esRef.current){ esRef.current.close(); esRef.current = null; } const ev = new EventSource(`${apiBase}/filemoon/progress/${uploadId}`); ev.addEventListener('progress', (e)=>{ try{ const d = JSON.parse(e.data||'{}'); if(typeof d.percent==='number'){ setProgress(d.percent); setProcessing(true); } }catch(_){} }); ev.addEventListener('done', (e)=>{ try{ const d = JSON.parse(e.data||'{}'); setProcessing(false); setProgress(100); }catch(_){} }); ev.addEventListener('error', (e)=>{ setProcessing(false); }); esRef.current = ev; }catch(e){}

  const xhr = new XMLHttpRequest(); xhr.open('POST', url);
  xhr.upload.onprogress = (ev)=>{
    if(ev.lengthComputable){
      const pct = Math.round((ev.loaded/ev.total)*100);
      setProgress(pct);
      // when client finished sending to our server, enter processing state
      if(pct >= 100){ setProcessing(true); }
    }
  };
  xhr.onload = async ()=>{
    // server responded (includes any server-side forwarding to Filemoon)
    setProcessing(false);
    setUploading(false);
    if(xhr.status>=200 && xhr.status<300){ setFile(null); setTitle(''); setProgress(100); await loadFiles(selectedFolder?.id); }
    else { const msg = xhr.responseText ? `Nahrávání selhalo: ${xhr.status} ${xhr.responseText}` : `Nahrávání selhalo: ${xhr.status}`; setError(msg); }
  };
    xhr.onerror = ()=>{ setUploading(false); setError('Síťová chyba při nahrávání.'); };
    // attach uploadId so server can correlate SSE
    fd.append('uploadId', uploadId);
    xhr.send(fd);
  }

  async function deleteFile(id){
    // open confirm modal for files
    const f = (files || []).find(x=>x.id===id);
    setShowConfirm({ kind: 'file', id, title: f ? f.title || f.id : id });
  }

  async function doDeleteFile(id){
    try{
      const r = await fetch(`${apiBase}/filemoon/files/${encodeURIComponent(id)}`, { method:'DELETE' });
      const txt = await r.text();
      if(!r.ok){
        let msg = txt;
        try{ const j = JSON.parse(txt); msg = j && (j.error || j.message) ? (j.error || j.message) : JSON.stringify(j); }catch(_){}
        console.warn('delete file failed', r.status, msg);
        setError(`Nelze smazat: ${r.status} ${msg}`);
        return;
      }
      await loadFiles(selectedFolder?.id);
    }catch(e){ console.error('doDeleteFile error', e); setError('Nelze smazat: ' + (e && e.message ? e.message : 'chyba')); }
  }

  async function moveFile(fileId, targetFolderId){ try{ const r = await fetch(`${apiBase}/filemoon/files/${encodeURIComponent(fileId)}`, { method:'PUT', headers:{'content-type':'application/json'}, body: JSON.stringify({ folderId: targetFolderId }) }); if(!r.ok) throw new Error('move'); await loadFiles(selectedFolder?.id); }catch(e){ setError('Nelze přesunout.'); } }

  function openMoveSelect(id){ const el = moveRefs.current[id]; if(!el) return; try{ if(typeof el.showPicker === 'function') { el.showPicker(); } else { el.focus(); el.click(); } }catch(e){ try{ el.focus(); el.click(); }catch(_){} } }

  return (
    <PageWrap>
      <Inner>
        <Header>
          <Title>Nahrávání a správa souborů</Title>
          <Subtitle>Spravujte složky a nahrávejte videa přes Filemoon</Subtitle>
        </Header>

        <Grid>
          <Sidebar>
            <SectionTitle>Složky</SectionTitle>
            <RowControls>
              <InputWrapper onClick={() => folderNameRef.current && folderNameRef.current.focus()}>
                <Input ref={folderNameRef} value={newFolderName} onChange={e=>setNewFolderName(e.target.value)} placeholder="Nová složka" />
              </InputWrapper>
              <IconButton onClick={createFolder} title="Vytvořit složku"><MdCreateNewFolder /></IconButton>
            </RowControls>
            <FolderList>
              {folders.map(f=> (
                <FolderRow key={f.id} onClick={()=>setSelectedFolder(f)} active={selectedFolder && selectedFolder.id===f.id}>
                  <div>{f.name}</div>
                  <div style={{ display:'flex', gap:8}}>
                    <SmallIconButton title="Přejmenovat" onClick={(e)=>{ e.stopPropagation(); setShowRename({ kind:'folder', id: f.id, currentName: f.name }); }}><MdEdit /></SmallIconButton>
                    <SmallIconButton title="Smazat" danger onClick={(e)=>{ e.stopPropagation(); setShowConfirm({ kind:'folder', id: f.id, title: f.name }); }}><MdDelete /></SmallIconButton>
                  </div>
                </FolderRow>
              ))}
            </FolderList>
          </Sidebar>

          <Main>
            <SectionTitle>Upload</SectionTitle>
            <Card>
              <Label>Nahrát do: <strong>{selectedFolder ? selectedFolder.name : 'Bez složky'}</strong></Label>
              <FilePicker>
                <input ref={fileInputRef} type="file" onChange={handleFileChange} style={{ display:'none' }} />
                <ChooseFileButton onClick={() => fileInputRef.current && fileInputRef.current.click()}>Vybrat soubor</ChooseFileButton>
                <FileName>{file ? file.name : <span style={{opacity:0.7}}>Žádný soubor</span>}</FileName>
                {file && <ClearFileButton onClick={() => { setFile(null); if(fileInputRef.current) fileInputRef.current.value = null; }}>✕</ClearFileButton>}
              </FilePicker>
              <div style={{ marginTop:8 }}>
                <InputWrapper onClick={() => document.getElementById('upload-title') && document.getElementById('upload-title').focus()}>
                  <Input id="upload-title" value={title} onChange={e=>setTitle(e.target.value)} placeholder="Název videa" />
                </InputWrapper>
              </div>
              <div style={{ marginTop:12, display:'flex', gap:8 }}>
                <PrimaryButton onClick={upload} disabled={uploading}><MdCloudUpload style={{ marginRight:8 }} />Nahrát</PrimaryButton>
                <SecondaryButton onClick={()=>{ setFile(null); setTitle(''); if(fileInputRef.current) fileInputRef.current.value = null; }}>Vyčistit</SecondaryButton>
              </div>
              {error && <ErrorText>{error}</ErrorText>}

              <ProgressWrap>
                <ProgressBar>
                  <ProgressFill style={{ width: `${progress}%` }} className={processing ? 'processing' : ''} />
                </ProgressBar>
                <ProgressLabel>
                  {(
                    uploading || processing
                  ) ? `Nahrávání ${progress}%${processing ? ' (zpracovávám na serveru...)' : ''}` : (progress ? `Poslední nahrání: ${progress}%` : '')}
                </ProgressLabel>
              </ProgressWrap>
            </Card>
          </Main>

          <Aside>
            <SectionTitle>Soubory ve složce</SectionTitle>
            <FilesCard>
              {(files||[]).length===0 && <Empty>Žádné soubory</Empty>}
              {(files||[]).map(it=> (
                <FileRow key={it.id}>
                  <FileMeta>
                    <div style={{ fontWeight:700 }}>{it.title || it.id}</div>
                    <div style={{ fontSize:12, opacity:0.8 }}>{it.host || it.id}</div>
                  </FileMeta>
                  <FileActions>
                    <MoveSelect ref={el => moveRefs.current[it.id] = el} onChange={(e)=>{ const targetFolder = e.target.value; if(targetFolder) moveFile(it.id, targetFolder); }} defaultValue="">
                      <option value="">Přesunout do...</option>
                      {folders.filter(f=>f.id!==selectedFolder?.id).map(f=> <option key={f.id} value={f.id}>{f.name}</option>)}
                    </MoveSelect>
                    <IconButton title="Otevřít výběr" onClick={()=>openMoveSelect(it.id)}><MdDriveFileMove /></IconButton>
                    <SmallIconButton onClick={()=>setShowRename({ kind:'file', id: it.id, currentName: it.title || '' })}><MdEdit /></SmallIconButton>
                    <SmallIconButton danger onClick={()=>setShowConfirm({ kind:'file', id: it.id, title: it.title || it.id })}><MdDelete /></SmallIconButton>
                  </FileActions>
                </FileRow>
              ))}
            </FilesCard>
          </Aside>
        </Grid>
        {/* Confirm modal */}
        {showConfirm && (
          <ModalOverlay onClick={()=>setShowConfirm(null)}>
            <ModalBox onClick={e=>e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle>Potvrdit akci</ModalTitle>
                <CloseBtn onClick={()=>setShowConfirm(null)}><MdClose /></CloseBtn>
              </ModalHeader>
              <ModalBody>Opravdu chcete smazat {showConfirm.kind === 'folder' ? 'složku' : 'soubor'} <strong>{showConfirm.title}</strong>?</ModalBody>
              <ModalActions>
                <SecondaryButton onClick={()=>setShowConfirm(null)}>Zrušit</SecondaryButton>
                <PrimaryButton onClick={async ()=>{
                  const { kind, id } = showConfirm;
                  setShowConfirm(null);
                  if(kind === 'folder'){
                    try{ const r = await fetch(`${apiBase}/filemoon/folders/${encodeURIComponent(id)}`, { method:'DELETE' }); if(r.ok){ if(selectedFolder && selectedFolder.id===id) setSelectedFolder(null); await loadFolders(); } else setError('Nelze smazat složku'); }catch(e){ setError('Nelze smazat složku'); }
                  } else {
                    await doDeleteFile(id);
                  }
                }}>Smazat</PrimaryButton>
              </ModalActions>
            </ModalBox>
          </ModalOverlay>
        )}

        {/* Rename modal */}
        {showRename && (
          <ModalOverlay onClick={()=>setShowRename(null)}>
            <ModalBox onClick={e=>e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle>Upravit název</ModalTitle>
                <CloseBtn onClick={()=>setShowRename(null)}><MdClose /></CloseBtn>
              </ModalHeader>
              <ModalBody>
                <div style={{ marginBottom:8 }}>Nový název</div>
                <input id="rename-input" defaultValue={showRename.currentName} style={{ width:'100%', padding:8, borderRadius:8, border:'1px solid rgba(255,255,255,0.06)', background:'#071014', color:'#e6eef8' }} />
              </ModalBody>
              <ModalActions>
                <SecondaryButton onClick={()=>setShowRename(null)}>Zrušit</SecondaryButton>
                <PrimaryButton onClick={async ()=>{
                  const inp = document.getElementById('rename-input');
                  const val = inp && inp.value;
                  if(!val) return setError('Název nesmí být prázdný');
                  const { kind, id } = showRename;
                  setShowRename(null);
                  if(kind === 'folder'){
                    await renameFolder(id, val);
                  } else {
                    try{ const r = await fetch(`${apiBase}/filemoon/files/${encodeURIComponent(id)}`, { method:'PUT', headers:{'content-type':'application/json'}, body: JSON.stringify({ title: val }) }); if(!r.ok) throw new Error('bad'); await loadFiles(selectedFolder?.id); }catch(e){ setError('Nelze upravit název'); }
                  }
                }}>Uložit</PrimaryButton>
              </ModalActions>
            </ModalBox>
          </ModalOverlay>
        )}
      </Inner>
    </PageWrap>
  );
}

// Styled components
const PageWrap = styled.div`
  padding: 36px 18px 20px 18px; /* a little bit lower on the page */
  background: linear-gradient(180deg,#0f1720,#11131a);
  min-height: 100vh;
  color: #e6eef8;
`;

const Inner = styled.div`
  max-width: none;
  width: calc(100% - 36px);
  margin: 0 auto;
`;

const Header = styled.div`
  display:flex;
  flex-direction:column;
  gap:6px;
`;
const Title = styled.h1`
  margin:0; font-size:20px;
`;
const Subtitle = styled.div`
  color: #bcd4ff; opacity:0.9; font-size:13px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 260px 1fr minmax(360px, 1fr); /* right column stretches to the right */
  gap: 16px;
  margin-top: 12px;
`;

const Sidebar = styled.div``;
const Main = styled.div``;
const Aside = styled.div``;

const SectionTitle = styled.div`
  font-weight:700; margin-bottom:8px;
`;

const RowControls = styled.div`
  display:flex; gap:8px; align-items:center;
`;
const Input = styled.input`
  padding:8px 10px; border-radius:8px; border:1px solid rgba(255,255,255,0.06); background:#071014; color:inherit; width:100%;
`;
const IconButton = styled.button`
  background: rgba(255,255,255,0.04); border: none; color: #e6eef8; padding:8px; border-radius:8px; cursor:pointer;
`;
const SmallIconButton = styled(IconButton)`
  padding:6px; font-size:14px; ${p=>p.danger?`background:#6b1d1d; color:#fff;` : ''}
`;

const InputWrapper = styled.div`
  width:100%;
  cursor: text;
`;

const FilePicker = styled.div`
  display:flex; align-items:center; gap:12px;
`;
const ChooseFileButton = styled.button`
  background: rgba(255,255,255,0.04); border:1px solid rgba(255,255,255,0.06); color:#e6eef8; padding:8px 12px; border-radius:8px; cursor:pointer;
`;
const FileName = styled.div`
  color:#cfe6ff; opacity:0.95; font-size:14px; flex:1; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;
`;
const ClearFileButton = styled.button`
  background: transparent; border: none; color:#e6eef8; cursor:pointer; font-size:16px; padding:6px;
`;

// Modal styles
const ModalOverlay = styled.div`
  position: fixed; inset:0; background: rgba(10,10,12,0.6); display:flex; align-items:center; justify-content:center; z-index:110000;
`;
const ModalBox = styled.div`
  width: min(560px, 96%); background: linear-gradient(180deg,#0f1720,#11131a); border-radius:12px; padding:16px; border:1px solid rgba(255,255,255,0.06); color:#e6eef8;
`;
const ModalHeader = styled.div`
  display:flex; align-items:center; justify-content:space-between; margin-bottom:8px;
`;
const ModalTitle = styled.div`
  font-weight:700;
`;
const CloseBtn = styled.button`
  background:transparent; border:0; color:#e6eef8; cursor:pointer; font-size:18px;
`;
const ModalBody = styled.div`
  margin-bottom:12px;
`;
const ModalActions = styled.div`
  display:flex; gap:8px; justify-content:flex-end;
`;

const FolderList = styled.div`
  margin-top:12px; border-radius:8px; overflow:auto; max-height:520px; border:1px solid rgba(255,255,255,0.04); background: rgba(255,255,255,0.02);
`;
const FolderRow = styled.div`
  padding:10px; display:flex; justify-content:space-between; align-items:center; cursor:pointer; border-bottom:1px solid rgba(255,255,255,0.02);
  background: ${p => p.active ? 'rgba(255,255,255,0.02)' : 'transparent'};
`;

const Card = styled.div`
  background: linear-gradient(180deg,#0f1720,#11131a); border-radius:12px; padding:14px; border:1px solid rgba(255,255,255,0.06);
`;
const Label = styled.div`
  color:#cfe6ff; margin-bottom:6px;
`;

const PrimaryButton = styled.button`
  background:#2d8a34; color:#fff; border:0; padding:8px 12px; border-radius:8px; cursor:pointer; display:inline-flex; align-items:center;
`;
const SecondaryButton = styled.button`
  background:transparent; color:#cfe6ff; border:1px solid rgba(255,255,255,0.06); padding:8px 12px; border-radius:8px; cursor:pointer;
`;
const ErrorText = styled.div`
  color:#ff6b6b; margin-top:8px;
`;

const ProgressWrap = styled.div`
  margin-top:12px;
`;
const ProgressBar = styled.div`
  height:10px; background:#0b0e10; border-radius:6px; overflow:hidden;
`;
const ProgressFill = styled.div`
  height:100%; background:#2d8a34; width:0%; transition: width 120ms linear;
`;
const ProgressLabel = styled.div`
  margin-top:6px; font-size:13px; opacity:0.8;
`;

const FilesCard = styled.div`
  border-radius:8px; border:1px solid rgba(255,255,255,0.04); background: rgba(255,255,255,0.02); max-height:520px; overflow:auto; padding:8px;
`;
const FileRow = styled.div`
  display:flex; gap:8px; align-items:center; padding:10px; border-bottom:1px solid rgba(255,255,255,0.02);
`;
const FileMeta = styled.div`
  flex:1;
`;
const FileActions = styled.div`
  display:flex; gap:8px; align-items:center;
`;
const MoveSelect = styled.select`
  /* visually hidden but still focusable so we can programmatically open it */
  position: absolute; width:1px; height:1px; padding:0; margin:0; opacity:0; border:0; background:transparent; color:inherit;
`;

const Empty = styled.div`
  padding:12px; color:#cfe6ff; text-align:center;
`;

const CenteredMessage = styled.div`
  padding:20px; color:#e6eef8;
`;
